import React, { useEffect, useState, useMemo } from 'react'
import { Modal, Icon, Tooltip, Avatar } from 'antd'
import { connect } from 'react-redux'
import moment from 'moment'
import { getAssetImages, getAssetLastImage } from '../../appRedux/actions/Asset'
import PhotoLightbox from './PhotoLightbox'
import { AiScore } from './AiBasis'

/**
 * PvLinkPicker
 *
 * Choosing what a PV item actually is. A PV is something found in the audit with
 * no asset code or QR, so the scan could not match it: it must either be LINKED
 * to an existing asset this audit has not otherwise accounted for, or used to
 * CREATE a new asset.
 *
 * The decision is visual — "is this stool the same physical stool as STS-4026?" —
 * so the PV's own photo is pinned in the rail and stays on screen the whole time,
 * with each candidate rendered as a photo card beside it. The previous picker
 * showed the PV as text only, which made the comparison impossible.
 *
 * Candidates arrive pre-bucketed by `pvCandidates` in the container: most
 * specific rule first, each asset in the first rule it satisfies, and assets that
 * cannot be this item already removed. Each card spells out WHICH fields agree,
 * so the reason a candidate is offered is visible rather than inferred from a
 * rule name.
 *
 * Data reliability:
 *  - Nothing is hidden silently: the count of assets withheld, and why, is
 *    printed under the candidates.
 *  - Only the first CARD_CAP candidates of a group are rendered. Each card loads
 *    that asset's thumbnail, so rendering a whole 86-asset group would fire 86
 *    requests; the remainder is revealed on request and the true count is always
 *    shown.
 *  - Linking an asset whose model differs overwrites the asset's model with the
 *    PV's. That consequence is stated on the card before the click, rather than
 *    in a confirm dialog after it.
 */

const CARD_CAP = 12

const RULE_FIELDS = {
  mlc: { model: true, loc: true, sub: true },
  ml: { model: true, loc: true, sub: false },
  slc: { model: false, loc: true, sub: true },
  sl: { model: false, loc: true, sub: false },
  m: { model: true, loc: false, sub: false },
  s: { model: false, loc: false, sub: false }
}

// A rule that agrees on location is worth comparing photos against; one that only
// agrees on model or sub-type is a much weaker signal.
const isStrong = (key) => ['mlc', 'ml', 'slc', 'sl'].includes(key)

const Tick = ({ ok }) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round">
    {ok ? <path d="M20 6L9 17l-5-5" /> : <><path d="M18 6L6 18" /><path d="M6 6l12 12" /></>}
  </svg>
)

const PvLinkPicker = ({
  visible, record, candidates, locName, clientName,
  onLink, onNewAsset, onIgnore, onClose, loading,
  jevPanel, jevDocs, jevMatch, ...props
}) => {
  // The picker serves PV items and duplicate AST rows: a duplicate can be a wrong
  // code entry, i.e. an unidentified item wearing someone else's code.
  const isAst = !!(record && record.source === 'AST')
  const subjectCode = record ? (isAst ? record.assetCode : record.tempAssetCode) : ''
  const subjectVoucher = record ? (isAst ? record.astNo : record.pvNo) : ''
  const subjectWhen = record ? (isAst ? record.time : record.date) : null
  const subjectModel = record
    ? (isAst ? (record.matchedAsset && record.matchedAsset.assetModel) : record.assetModel)
    : null
  const subjectSub = record
    ? (isAst ? (record.matchedAsset && record.matchedAsset.equipmentSubType) : record.equipmentSubType)
    : null

  const groups = (candidates && candidates.groups) || []
  const excluded = (candidates && candidates.excluded) || { ast: 0, pv: 0, created: 0, sold: 0, total: 0 }

  const [activeKey, setActiveKey] = useState(null)
  const [showAll, setShowAll] = useState(false)
  // Zoom viewer: either the PV's own photos, or one candidate asset's history.
  const [zoom, setZoom] = useState(null) // { kind:'pv' } | { kind:'asset', asset }
  const [zoomIdx, setZoomIdx] = useState(0)

  const pvPhotos = useMemo(
    () => ((record && Array.isArray(record.photos)) ? record.photos.filter(p => p && p.url) : []),
    [record])

  // A candidate's photos are only fetched when its card is actually zoomed —
  // never for the whole grid.
  useEffect(() => { setZoomIdx(0) }, [zoom && zoom.kind, zoom && zoom.asset && zoom.asset._id])

  const zoomAsset = zoom && zoom.kind === 'asset' ? zoom.asset : null
  useEffect(() => {
    if (zoomAsset && !props.assetImages[zoomAsset._id]) props.getAssetImages(zoomAsset._id)
  }, [zoomAsset]) // eslint-disable-line react-hooks/exhaustive-deps

  const zoomImages = useMemo(() => {
    if (!zoom) return []
    if (zoom.kind === 'pv') {
      return pvPhotos.map((p, n) => ({
        url: p.url,
        caption: `${subjectCode} — found in audit`,
        sub: [subjectVoucher, subjectWhen ? moment(subjectWhen).format('DD MMM YYYY, HH:mm') : null]
          .filter(Boolean).join(' · ') + (pvPhotos.length > 1 ? ` · photo ${n + 1}` : '')
      }))
    }
    const a = zoom.asset
    return ((props.assetImages[a._id]) || []).filter(im => im && im.url).map(im => ({
      url: im.url,
      caption: `${a.assetCode} — register photo`,
      sub: [im.voucher, im.time ? moment(im.time).format('DD MMM YYYY, HH:mm') : null,
        [im.location, im.client].filter(Boolean).join(' / ')].filter(Boolean).join(' · ')
    }))
  }, [zoom, pvPhotos, props.assetImages, record])

  // Open on the most specific group that has anything in it.
  useEffect(() => {
    setActiveKey(groups.length ? groups[0].key : null)
    setShowAll(false)
  }, [record && record._uid]) // eslint-disable-line react-hooks/exhaustive-deps

  // When the AI search finds a match, show its group (all cards if it sits beyond the
  // cap) and scroll its card into view; the card is highlighted below.
  const matchId = jevMatch ? jevMatch.assetId : null
  useEffect(() => {
    if (!jevMatch) return
    const g = groups.find(x => x.key === jevMatch.groupKey)
    if (!g) return
    setActiveKey(g.key)
    const idx = g.assets.findIndex(a => String(a._id) === jevMatch.assetId)
    if (idx >= CARD_CAP) setShowAll(true)
    setTimeout(() => {
      const el = document.getElementById('pvcard-' + jevMatch.assetId)
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }, 50)
  }, [matchId]) // eslint-disable-line react-hooks/exhaustive-deps

  const active = useMemo(
    () => groups.find(g => g.key === activeKey) || groups[0] || null,
    [groups, activeKey])

  const shown = useMemo(
    () => (active ? (showAll ? active.assets : active.assets.slice(0, CARD_CAP)) : []),
    [active, showAll])

  // One thumbnail request per RENDERED card, which is why the grid is capped.
  const shownIds = shown.map(a => String(a._id)).join(',')
  useEffect(() => {
    if (!visible) return
    shown.forEach(a => { if (!props.assetLastImage[a._id]) props.getAssetLastImage(a._id) })
  }, [shownIds, visible]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!record) return null

  const pvModel = subjectModel && subjectModel._id
  const hiddenInGroup = active ? active.assets.length - shown.length : 0
  const thumbOf = (a) => {
    const im = props.assetLastImage[a._id]
    return (im && im[0] && im[0].url) || null
  }

  const card = (a) => {
    const f = RULE_FIELDS[active.key] || {}
    const overwrite = !!(pvModel && a.assetModel && String(a.assetModel._id) !== String(pvModel))
    return (
      <div className={'ccard' + (isStrong(active.key) ? ' best' : '') + (matchId === String(a._id) ? ' jvmatch' : '')}
        key={a._id} id={'pvcard-' + a._id}>
        <div className="cphoto zoomable" onClick={() => setZoom({ kind: 'asset', asset: a })}
          title="Click to zoom">
          {thumbOf(a)
            ? <><img src={thumbOf(a)} alt={a.assetCode} /><span className="pc-zoom"><Icon type="zoom-in" /></span></>
            : <Avatar shape="square" size={64} icon="file-image" />}
        </div>
        <div className="cbody">
          <div>
            <div className="ccode">{a.assetCode}
              {jevDocs && jevDocs[String(a._id)] && (
                <AiScore prob={jevDocs[String(a._id)].sameProbability} assetCode={a.assetCode}
                  state={jevDocs[String(a._id)].request && jevDocs[String(a._id)].request.state}
                  className={'jv-badge' + (matchId === String(a._id) ? ' on' : '')} />
              )}
            </div>
            <div className="cmeta">
              {[a.altName, a.ownerCode].filter(Boolean).join(' · ') || '—'}
            </div>
          </div>
          <div className="cmeta">
            <div>On PV date: <b>{locName(a._onDateLocation != null ? a._onDateLocation : a.location) || <em>—</em>}</b>
              {' '}/ <b>{clientName(a._onDateClient != null ? a._onDateClient : a.client) || '—'}</b></div>
            <div>Current: <b>{locName(a.location) || <em>—</em>}</b>
              {' '}/ <b>{clientName(a.client) || '—'}</b></div>
          </div>
          <div className="crules">
            <span className={'crule ' + (f.model ? 'y' : 'n')}>
              <Tick ok={!!f.model} />{f.model ? 'Model matches' : 'Model differs'}
            </span>
            <span className={'crule ' + (f.loc ? 'y' : 'n')}>
              <Tick ok={!!f.loc} />{f.loc ? 'Location matches' : 'Location differs'}
            </span>
            <span className={'crule ' + (f.sub ? 'y' : 'n')}>
              <Tick ok={!!f.sub} />{f.sub ? 'Sub-location matches' : 'Sub-location differs'}
            </span>
          </div>
          {overwrite && (
            <div className="cwarn">
              Linking overwrites this asset's model with the PV's —{' '}
              <b>{subjectModel && subjectModel.name}</b>.
            </div>
          )}
          <div className="cfoot">
            <button className={'act' + (isStrong(active.key) ? ' rec' : '')}
              onClick={() => onLink(a, overwrite)}>
              Link {overwrite ? 'and overwrite' : ''}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <Modal
      visible={visible} onCancel={onClose} footer={null} width={1400} centered destroyOnClose
      bodyStyle={{ padding: 0 }} className="rcx-pv-modal"
      title={
        <div className="pc-head">
          <span className="pill new">{isAst ? 'Duplicate scan (AST)' : 'New item (PV)'}</span>
          <span className="pc-code">{subjectCode}</span>
          <span className="pc-meta">
            {[subjectModel && subjectModel.name,
              subjectSub && subjectSub.name,
              !isAst && record.assetType && record.assetType.name].filter(Boolean).join(' · ')}
          </span>
        </div>
      }
    >
      <div className="rcx">
        <div className="m2body">
          <div className="rail">
            <div className="pc-lbl scan">Found in audit</div>
            <div className={'railplate' + (pvPhotos.length ? ' zoomable' : '')}
              onClick={pvPhotos.length ? () => setZoom({ kind: 'pv' }) : undefined}
              title={pvPhotos.length ? 'Click to zoom' : undefined}>
              {pvPhotos.length
                ? <><img src={pvPhotos[0].url} alt={record.tempAssetCode} />
                    <span className="pc-zoom"><Icon type="zoom-in" /></span>
                    {pvPhotos.length > 1 && <span className="railcount mono">{pvPhotos.length}</span>}</>
                : <div className="pc-none"><Icon type="picture" /><span>no photo captured</span></div>}
            </div>
            <div className="railfield">
              <b>{subjectVoucher || '—'}</b>
              {subjectWhen ? ` · ${moment(subjectWhen).format('DD MMM YYYY, HH:mm')}` : ''}
              <br />{locName(record.location) || <em>location not recorded</em>}
              <br />Sub-location <b>{clientName(record.client) || '—'}</b>
            </div>
            <div className="railfield bordered">
              Model <b>{(subjectModel && subjectModel.name) || '—'}</b><br />
              Sub-type <b>{(subjectSub && subjectSub.name) || '—'}</b><br />
              {!isAst && <>Type <b>{(record.assetType && record.assetType.name) || '—'}</b></>}
            </div>
            <div className="railfield faint">
              {isAst
                ? <>This code was scanned more than once in this audit. If that was a wrong code
                    entry, this is a different physical asset — link it to the one it really is, or
                    create a new asset. Equipment shown is what the scanned code implies.</>
                : <>No asset code or QR was on this item, so the scan could not match it.</>}
            </div>
          </div>

          <div className="cand">
            {jevPanel}
            {groups.length ? (
              <>
                <div className="mq" role="group" aria-label="Match quality">
                  {groups.map(g => (
                    <Tooltip key={g.key} title={isStrong(g.key)
                      ? 'Agrees on location — worth comparing photos.'
                      : 'Only the model or sub-type agrees; the location does not.'}>
                      <button className={'mqchip' + (g.key === (active && active.key) ? ' on' : '')
                        + (isStrong(g.key) ? '' : ' weak')}
                        onClick={() => { setActiveKey(g.key); setShowAll(false) }}>
                        {g.name} <b className="b tnum">{g.count}</b>
                      </button>
                    </Tooltip>
                  ))}
                </div>

                <div className="cgrid">{shown.map(card)}</div>

                {hiddenInGroup > 0 && (
                  <div className="morebar">
                    Showing {shown.length} of {active.assets.length}.
                    <button className="act mute" onClick={() => setShowAll(true)}>
                      Show {hiddenInGroup} more
                    </button>
                    <span className="hintx">Each card loads that asset's photo, so they are revealed on request.</span>
                  </div>
                )}
              </>
            ) : (
              <div className="nocand">
                <Icon type="inbox" />
                <div className="big">No asset can be linked</div>
                <div className="sub">
                  Nothing in the register shares this item's model or sub-type once assets already
                  accounted for in this audit are set aside. Create a new asset, or ignore the item.
                </div>
              </div>
            )}
          </div>
        </div>

        {excluded.total > 0 && (
          <div className="excl">
            <Icon type="info-circle" />
            <span>
              <b className="tnum">{excluded.total}</b> matching asset{excluded.total === 1 ? '' : 's'} not offered
              {excluded.ast > 0 && <> · <b className="tnum">{excluded.ast}</b> already found in this audit</>}
              {excluded.pv > 0 && <> · <b className="tnum">{excluded.pv}</b> linked to another PV</>}
              {excluded.created > 0 && <> · <b className="tnum">{excluded.created}</b> created by this audit</>}
              {excluded.sold > 0 && <> · <b className="tnum">{excluded.sold}</b> at the SOLD location</>}
            </span>
          </div>
        )}

        <div className="m2f">
          <span className="hintx">Nothing here matches?</span>
          <button className="act" onClick={onNewAsset}>Create new asset</button>
          <button className="act mute" onClick={onIgnore}>Ignore</button>
          <span className="m2f-sp" />
          <button className="act mute" onClick={onClose}>Close</button>
        </div>

        <PhotoLightbox
          visible={!!zoom}
          images={zoomImages}
          index={zoomIdx}
          onIndex={setZoomIdx}
          onClose={() => setZoom(null)}
        />
      </div>
    </Modal>
  )
}

const mapStateToProps = ({ asset }) => ({
  assetImages: asset.assetImages,
  assetLastImage: asset.assetLastImage
})

export default connect(mapStateToProps, { getAssetImages, getAssetLastImage })(PvLinkPicker)
